export const API_ERROR_CODE = {
  NOT_AUTHENTICATED: "not_authenticated",
  PERMISSION_DENIED: "permission_denied"
};

export const API_ERROR_DETAIL_KEYWORD = {
  PREMIUM: "premium",
  PERMISSION: "permission"
};

export const ACCESS_DENIED_REASON = {
  NOT_AUTHENTICATED: API_ERROR_CODE.NOT_AUTHENTICATED,
  PERMISSION: API_ERROR_DETAIL_KEYWORD.PERMISSION
};

export function getApiErrorStatus(error) {
  return (
    error?.request?.status ??
    error?.response?.status ??
    error?.response?.data?.status
  );
}

export function getApiErrorBody(error) {
  const axiosData = error?.response?.data;

  if (axiosData && typeof axiosData === "object") {
    return axiosData;
  }

  const rawResponse = error?.request?.response;

  if (typeof rawResponse !== "string" || !rawResponse.length) {
    return null;
  }

  try {
    return JSON.parse(rawResponse);
  } catch (e) {
    return null;
  }
}

function getApiErrorEntries(body) {
  if (!body || typeof body !== "object" || !Array.isArray(body.errors)) {
    return [];
  }

  return body.errors;
}

function getApiErrorCodes(error) {
  const body = getApiErrorBody(error);
  const codes = getApiErrorEntries(body)
    .map(entry =>
      String(entry?.code ?? "")
        .toLowerCase()
        .trim()
    )
    .filter(Boolean);

  if (body?.code) {
    codes.push(
      String(body.code)
        .toLowerCase()
        .trim()
    );
  }

  return codes;
}

export function getApiErrorDetail(error) {
  const body = getApiErrorBody(error);

  if (typeof body?.detail === "string") {
    return body.detail;
  }

  const entryWithDetail = getApiErrorEntries(body).find(
    entry => typeof entry?.detail === "string" && entry.detail.length
  );

  if (entryWithDetail) {
    return entryWithDetail.detail;
  }

  const rawResponse = error?.request?.response;

  return typeof rawResponse === "string" ? rawResponse : "";
}

const normalizePermissions = permissions => {
  if (Array.isArray(permissions) && permissions.length) {
    return permissions.filter(Boolean);
  }

  if (typeof permissions === "string" && permissions.length) {
    return [permissions];
  }

  return null;
};

export function getPermissionsFromApiError(error) {
  if (!error) {
    return null;
  }

  const body = getApiErrorBody(error);

  if (!body || typeof body !== "object") {
    return null;
  }

  return normalizePermissions(body.permissions);
}

export function getAccessDeniedReasonFromApiError(error) {
  const body = getApiErrorBody(error);
  const codes = getApiErrorCodes(error);
  const detail = getApiErrorDetail(error).toLowerCase();

  if (
    codes.includes(API_ERROR_CODE.NOT_AUTHENTICATED) ||
    detail.includes(API_ERROR_CODE.NOT_AUTHENTICATED) ||
    body?.not_authenticated === true
  ) {
    return ACCESS_DENIED_REASON.NOT_AUTHENTICATED;
  }

  return ACCESS_DENIED_REASON.PERMISSION;
}

export function isAccessDeniedApiError(error) {
  if (!error) {
    return false;
  }

  const status = getApiErrorStatus(error);

  if (status === 401 || status === 403) {
    return true;
  }

  const codes = getApiErrorCodes(error);

  if (
    codes.includes(API_ERROR_CODE.PERMISSION_DENIED) ||
    codes.includes(API_ERROR_CODE.NOT_AUTHENTICATED)
  ) {
    return true;
  }

  const detail = getApiErrorDetail(error).toLowerCase();

  return (
    detail.includes(API_ERROR_DETAIL_KEYWORD.PREMIUM) ||
    detail.includes(API_ERROR_DETAIL_KEYWORD.PERMISSION) ||
    detail.includes(API_ERROR_CODE.NOT_AUTHENTICATED)
  );
}

export default isAccessDeniedApiError;
